import React, { ReactElement, useMemo } from 'react';

import keyBy from 'lodash/keyBy';

import { classnames, margin } from '@lumapps/classnames';
import { Placement, ProgressCircular, Size, Theme, ThemeProvider, useTheme } from '@lumapps/lumx/react';
import { Paragraph } from '@lumapps/wrex-typography/components/blocks/Paragraph';
import { isMention } from '@lumapps/wrex-user-mention/utils/isMention';

import { WREX_VIEW_CLASSNAME } from '../../constants';
import { renderElements, renderLeafs } from '../../slate/render';
import { isText } from '../../slate/utils/isText';
import { ElementRender, MarkRender, RenderElementType, RenderLeafType, Wrex } from '../../types';

import './index.scss';

export interface ContentViewProps {
    /**
     * Forwarded HTML id
     */
    id?: string;

    /**
     * Forwarded HTML class
     */
    className?: string;

    /**
     * Slate content.
     */
    content: Wrex.Nodes;

    /**
     * Element renderer functions.
     */
    elements: ElementRender<any, any>[];

    /**
     * Mark renderer functions.
     */
    marks: MarkRender[];

    /**
     * Mention index.
     */
    mentionIndex?: Record<string, { fullName: string }>;

    /**
     * The Lumx theme.
     */
    theme?: Theme;

    /**
     * Whether text is currently streaming
     * Displays a circular progress indicator while true
     */
    isStreaming?: boolean;
}

const render = (
    nodes: Wrex.Nodes,
    renderElement: RenderElementType,
    renderLeaf: RenderLeafType,
    path: number[],
    mentionIndex?: Record<string, { fullName: string }>,
    // Streaming related parameters:
    isStreamingActive?: boolean, // Whether streaming is globally active
    isPotentialLastBranch?: boolean, // Whether the current path of nodes is potentially the one to bear the indicator
): Array<ReactElement | undefined> => {
    return (nodes ?? []).map((node, idx) => {
        const newPath = [...path, idx];
        const uniqueKey = newPath.join('-');
        const attributes: any = { key: uniqueKey };

        if (isText(node)) {
            const text = node as Wrex.Text;
            return renderLeaf({
                text,
                leaf: text,
                attributes,
                children: text.text,
            });
        }

        const newNode = { ...node };

        if (mentionIndex && isMention(newNode)) {
            const userDetails = mentionIndex[newNode.user.id];
            newNode.user = {
                ...newNode.user,
                ...userDetails,
            };
        }

        const isCurrentNodeLastInThisList = idx === nodes.length - 1;
        let showIndicatorWithThisElement = false;
        let passPotentialLastBranchToChildren = false;

        if (isStreamingActive && isPotentialLastBranch && isCurrentNodeLastInThisList) {
            // This node is the last in its list and on a path that could bear the indicator.
            // Check if it's structurally terminal (no element children or only text children).
            const hasNoElementChildren = !node.children || node.children.length === 0;
            const hasOnlyTextChildren = node.children && node.children.length > 0 && node.children.every(isText);

            if (hasNoElementChildren || hasOnlyTextChildren) {
                // This element is where the indicator should appear.
                showIndicatorWithThisElement = true;
            } else {
                // This element has further structural children; pass the "last branch" status down.
                passPotentialLastBranchToChildren = true;
            }
        }

        const after = showIndicatorWithThisElement ? (
            <ProgressCircular
                size="xxs"
                className={margin(Placement.LEFT, Size.tiny)}
                key={`${uniqueKey}-indicator`}
                display="inline"
            />
        ) : null;

        return renderElement({
            element: newNode,
            attributes,
            children: [
                ...render(
                    node.children,
                    renderElement,
                    renderLeaf,
                    newPath,
                    mentionIndex,
                    isStreamingActive, // Pass global streaming state
                    passPotentialLastBranchToChildren, // Children are on the last branch only if this node was last and had children
                ),
                after,
            ].filter(Boolean),
        });
    });
};

/**
 * Render a slate content.
 */
export const ContentView: React.FC<ContentViewProps> = (props) => {
    const defaultTheme = useTheme() || Theme.light;
    const { id, className, content, elements, marks, mentionIndex, theme = defaultTheme, isStreaming = false } = props;
    const renderElement = useMemo(() => renderElements(Paragraph, keyBy(elements, 'displayName')), [elements]);
    const renderLeaf = useMemo(() => renderLeafs(keyBy(marks, 'displayName'), { viewMode: true }), [marks]);

    const renderContent = React.useMemo(() => {
        return render(
            content,
            renderElement,
            renderLeaf,
            [], // Initial path
            mentionIndex,
            isStreaming, // isStreamingActive
            isStreaming, // isPotentialLastBranch (initially true only if streaming is active)
        );
    }, [content, mentionIndex, renderElement, renderLeaf, isStreaming]);

    return (
        <ThemeProvider value={theme}>
            <div id={id} className={classnames(WREX_VIEW_CLASSNAME, className)}>
                {renderContent}
            </div>
        </ThemeProvider>
    );
};
